import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { FileUtility } from './FileUtility';

/**
 * Service for handling file downloads with automatic filename extraction from HTTP headers.
 * 
 * Example usage:
 * 
 * // In a service:
 * downloadInvoice(tripId: number): Observable<DownloadResult> {
 *   return this.downloadService.downloadFile(
 *     `${this.config.dataApiUrl}/finance/invoice/${tripId}`,
 *     'invoice.pdf'
 *   );
 * }
 * 
 * // In a component:
 * downloadInvoice() {
 *   this.financeService.downloadInvoice(this.tripId).subscribe(result => {
 *     FileUtility.saveBlob(result.blob!, result.filename);
 *   });
 * }
 * 
 * // Or use the convenience method:
 * downloadInvoice() {
 *   this.financeService.downloadInvoice(this.tripId).subscribe(result => {
 *     this.downloadService.triggerDownload(result.blob!, result.filename);
 *   });
 * }
 */

export interface DownloadResult {
  blob: Blob | null;
  filename: string;
}

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  constructor(private http: HttpClient) { }

  /**
   * Downloads a file and extracts the filename from the content-disposition header
   * @param url The URL to download from
   * @param defaultFilename The default filename to use if none is found in headers
   * @param options Additional HTTP options
   * @returns Observable with blob and filename
   */
  downloadFile(url: string, defaultFilename: string = 'download', options: any = {}): Observable<DownloadResult> {
    const httpOptions = {
      responseType: 'blob' as const,
      observe: 'response' as const,
      ...options
    };

    return this.http.get(url, httpOptions).pipe(
      map((response: any) => {
        const filename = this.extractFilenameFromHeaders(response.headers, defaultFilename);
        return {
          blob: response.body,
          filename: filename
        };
      })
    );
  }

  /**
   * Extracts filename from HTTP response headers
   * @param headers The HTTP headers
   * @param defaultFilename Default filename if none found
   * @returns The extracted filename
   */
  private extractFilenameFromHeaders(headers: HttpHeaders, defaultFilename: string): string {
    const contentDisposition = headers.get('content-disposition');
    
    if (!contentDisposition) {
      return defaultFilename;
    }

    // Try to extract filename from content-disposition header
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, '');
    }

    return defaultFilename;
  }

  /**
   * Creates a download link and triggers download
   * @param blob The file blob
   * @param filename The filename
   */
  triggerDownload(blob: Blob, filename: string): void {
    FileUtility.saveBlob(blob, filename);
  }
} 